import { useId, useState, type ReactNode } from 'react';
import { Box, Button, CloseIcon, Dialog, IconButton, Stack, Text } from '@citibox-panel/ui';
import { normalizeReason } from '../lib/reason';
import { ReasonField } from './ReasonField';

export interface AccountActionDialogProps {
  open: boolean;
  // Fallback accessible name; the heading names the dialog.
  label: string;
  heading: string;
  body: ReactNode;
  confirmLabel: string;
  confirmColor: 'error' | 'primary';
  withReason: boolean;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string | undefined) => void;
}

// The one-step confirmation of an action on the account's state — block or unblock — with the
// verb, the tint and the body as props. One step, no safety word and no checkbox: that double
// friction belongs to the baja, which is irreversible AND erases data, and these are neither.
//
// `withReason` adds the «Motivo» a courier account's action requires, and with it the focus moves
// to the field: safe, because in a textarea Enter adds a line and never confirms. The reason is
// handed over on one line, and the confirmation stays off until there is one.
export function AccountActionDialog({
  open,
  label,
  heading,
  body,
  confirmLabel,
  confirmColor,
  withReason,
  isPending,
  onCancel,
  onConfirm,
}: AccountActionDialogProps) {
  const titleId = useId();
  const [reason, setReason] = useState('');
  const missingReason = withReason && normalizeReason(reason) === '';

  // The guard has to live HERE. The façade always honours Escape, and `disableBackdropClose` only
  // covers the click outside, so this handler is the only place either door can be refused. The ✕
  // and «Cancelar» are refused by going `disabled`, which this cannot do.
  const handleClose = () => {
    if (isPending) return;
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      disableBackdropClose
      // On `onExited`, never on `onClose`: the paper stays on screen through the exit transition,
      // and a field emptying under the agent's eyes would read as their text being thrown away.
      onExited={() => {
        setReason('');
      }}
      label={label}
      labelledBy={titleId}
      title={
        // No warning glyph, unlike the baja's dialog: neither action erases anything.
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Text component="h2" variant="h6" id={titleId}>
            {heading}
          </Text>
          {/* Pushes the ✕ to the far edge, so the heading keeps the body's left margin. */}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton aria-label="Cerrar" onClick={handleClose} disabled={isPending}>
            <CloseIcon />
          </IconButton>
        </Stack>
      }
      actions={
        <>
          <Button
            color="neutral"
            sentenceCase
            // The escape takes the focus, never the action: an agent who presses Enter on a
            // dialog that has just opened must not execute it. With a reason, the field has it.
            autoFocus={!withReason}
            onClick={handleClose}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={confirmColor}
            sentenceCase
            loading={isPending}
            disabled={missingReason}
            onClick={() => {
              onConfirm(withReason ? normalizeReason(reason) : undefined);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {body}
        {withReason && <ReasonField value={reason} onChange={setReason} autoFocus />}
      </Stack>
    </Dialog>
  );
}
